// src/pages/creator-panel/Connector/GoogleReviews/GoogleReviewsConnector.js
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../../utils/axiosInstance";
import { hasPermission } from "../../../../utils/permissionUtils";
import {
    Box, Typography, Alert, Button as MuiButton, CircularProgress, LinearProgress
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import BarChartIcon from "@mui/icons-material/BarChart";
import ListGoogleReviews from "./ListGoogleReviews";

export default function GoogleReviewsConnector() {
    const [isConnected, setIsConnected] = useState(false);
    const [noBusiness, setNoBusiness] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [error, setError] = useState("");
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const location = useLocation();
    const navigate = useNavigate();

    const { chatbotId: paramsChatbotId } = useParams();

    // Permission states
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                setCanView(await hasPermission("connectors", "view"));
                setCanModify(await hasPermission("connectors", "add|edit"));
                setCanDelete(await hasPermission("connectors", "delete"));
            } catch (err) {
                console.error("Error checking permissions:", err);
            } finally {
                setCheckingPermissions(false);
            }
        };
        checkPermissions();
    }, []);

    const checkStatus = useCallback(async (showLoading = true) => {
        if (showLoading) setIsLoading(true);
        setError("");
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.get(`/api/google-reviews/status`, {
                timeout: 30000,
                params,
            });
            setIsConnected(response.data.connected);
            setNoBusiness(Boolean(response.data.no_business));
            if (!response.data.connected) {
                const search = new URLSearchParams(location.search);
                if (search.has("error") || search.has("connected")) {
                    navigate(location.pathname, { replace: true });
                }
            }
        } catch (err) {
            console.error("Error checking Google Reviews status:", err);
            setError("Failed to check connection status. Please try again.");
            setIsConnected(false);
        } finally {
            setIsLoading(false);
        }
    }, [location.pathname, location.search, navigate, paramsChatbotId]);

    useEffect(() => {
        document.title = "Google Reviews Connector | KlonIT";
        const search = new URLSearchParams(location.search);
        if (search.has("connected") && search.get("connected") === "true") {
            // Google OAuth just succeeded — show the connected UI right away and
            // auto-sync reviews once, so a freshly connected account pulls its
            // reviews without the user having to click "Sync Reviews" manually.
            navigate(location.pathname, { replace: true });
            setIsConnected(true);
            setIsLoading(false);
            checkStatus(false);
            handleSync();
        } else if (search.has("error")) {
            setError(`Authentication failed after redirect: ${search.get("error")}`);
            navigate(location.pathname, { replace: true });
        } else {
            checkStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleConnect = async () => {
        setIsConnecting(true);
        setError("");
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.get("/api/google-reviews/auth/start", { params });
            if (response.data.authorization_url) {
                window.location.href = response.data.authorization_url;
            } else {
                throw new Error("No authorization URL returned from the server.");
            }
        } catch (err) {
            console.error("Error starting Google Reviews authentication:", err);
            setError(err.response?.data?.message || "Failed to initiate connection. Please try again.");
            setIsConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm("Disconnect from Google Business Profile? Your synced reviews will remain, but you will need to reconnect to sync new ones.")) return;
        setIsConnecting(true);
        setError("");
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            await axiosInstance.post(`/api/google-reviews/disconnect`, {}, { params });
            setIsConnected(false);
            setError("Successfully disconnected from Google Business Profile.");
        } catch (err) {
            console.error("Error disconnecting Google Reviews:", err);
            setError(err.response?.data?.message || "Failed to disconnect. Please try again.");
        } finally {
            setIsConnecting(false);
        }
    };

    const handleSync = async () => {
        setIsSyncing(true);
        setError("");
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.post(`/api/google-reviews/sync`, {}, { params });
            setError(response.data.message || "Reviews synced successfully.");
            setRefreshTrigger((prev) => prev + 1);
        } catch (err) {
            console.error("Error syncing Google reviews:", err);
            setError(err.response?.data?.message || "Failed to sync reviews. Please try again.");
        } finally {
            setIsSyncing(false);
        }
    };

    const goToAnalytics = () => {
        navigate(`/chatbot/${paramsChatbotId}/connectors/google-reviews/analytics`);
    };

    if (checkingPermissions) {
        return <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}><Typography>Checking permissions...</Typography></Box>;
    }
    if (!canView) {
        return <Alert severity="error" sx={{ mt: 6 }}><strong>Access Denied:</strong> You don't have permission to view the Google Reviews Connector.</Alert>;
    }

    return (
        <div>
            <main className="content">
                <div className="container-fluid p-0">
                    <div className="mb-3">
                        <h1 className="h3 d-inline align-middle"><strong>Google Reviews Connector</strong></h1>
                    </div>

                    {error && (
                        <Alert
                            severity={error.includes("Success") || error.includes("successfully") || error.includes("complete") ? "success" : "error"}
                            sx={{ mt: 2 }}
                            onClose={() => setError("")}
                        >
                            {error}
                        </Alert>
                    )}

                    {isLoading && <Typography sx={{ p: 2 }}>Loading configuration...</Typography>}

                    {!isLoading && !isConnected && !canModify && (
                        <div className="card"><div className="card-body text-center"><p>You don't have permission to connect Google Business Profile. Please contact your administrator.</p></div></div>
                    )}

                    {!isLoading && !isConnected && canModify && (
                        <div className="card">
                            <div className="card-body text-center">
                                <p>Connect your Google Business Profile to sync customer reviews and ratings into your chatbot's knowledge.</p>
                                <button className="btn btn-lg btn-primary" onClick={handleConnect} disabled={isConnecting}>
                                    {isConnecting
                                        ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Connecting...</>
                                        : <><i className="fa fa-google me-2"></i> Connect Google Business Profile</>}
                                </button>
                            </div>
                        </div>
                    )}

                    {!isLoading && isConnected && (
                        <div className="card">
                            {isSyncing && <LinearProgress />}
                            <div className="card-header">
                                <div className="row align-items-center">
                                    <div className="col-lg-6">
                                        <p className="mb-0">Sync reviews from your Google Business Profile, then train them for your chatbot.</p>
                                        {noBusiness && <Typography variant="caption" color="warning.main">No business profile found yet — verify your business with Google.</Typography>}
                                        {isSyncing && <Typography variant="caption" color="primary">Fetching reviews from Google... Please wait.</Typography>}
                                    </div>
                                    <div className="col-lg-6">
                                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                            {canModify && (
                                                <MuiButton
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={handleSync}
                                                    disabled={isSyncing}
                                                    startIcon={isSyncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                                                >
                                                    {isSyncing ? "Syncing..." : "Sync Reviews"}
                                                </MuiButton>
                                            )}
                                            <MuiButton
                                                variant="outlined"
                                                color="primary"
                                                onClick={goToAnalytics}
                                                startIcon={<BarChartIcon />}
                                            >
                                                Analytics
                                            </MuiButton>
                                            {canModify && (
                                                <MuiButton
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={handleDisconnect}
                                                    disabled={isConnecting || isSyncing}
                                                    startIcon={isConnecting ? <CircularProgress size={20} /> : <i className="fa fa-unlink" />}
                                                >
                                                    {isConnecting ? "Disconnecting..." : "Disconnect"}
                                                </MuiButton>
                                            )}
                                        </Box>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <ListGoogleReviews
                                    refreshTrigger={refreshTrigger}
                                    chatbotId={paramsChatbotId}
                                    permission={{ canView, canModify, canDelete }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
